import type {
  iCustomerInvoice,
  iCustomerPaymentReceipt,
} from "@sdk/bookings/booking-model";
import { getReceiptHolidayAmount } from "@sdk/bookings/booking-model";
import { getNonVoidedReceipts } from "@sdk/bookings/customer-payment-helpers";
import type {
  iSupplierBill,
  SupplierBillPaymentStatus,
} from "@sdk/supplier-bills/supplier-bill-model";
import type { iSupplierBillPaymentReceipt } from "@sdk/supplier-bill-payment-receipts/supplier-bill-payment-receipt-model";

export function getInvoiceTotalAmount(invoice: iCustomerInvoice): number {
  return Number(invoice.pricingBreakdown?.totalCost ?? 0);
}

export function getInvoiceCurrency(invoice: iCustomerInvoice): string {
  return invoice.pricingBreakdown?.sellingCurrency ?? "GBP";
}

export function formatInvoiceLineSummary(invoice: iCustomerInvoice): string {
  const items = invoice.pricingBreakdown?.lineItems ?? [];
  if (items.length === 0) return "—";
  return items
    .map((item) => item.item || item.reference)
    .filter(Boolean)
    .slice(0, 2)
    .join(", ");
}

const LIST_AMOUNT_TOLERANCE = 0.005;

export function formatInvoiceSummaryPrimary(invoice: iCustomerInvoice): string {
  const items = invoice.pricingBreakdown?.lineItems ?? [];
  if (items.length === 0) return "—";
  if (items.length === 1) {
    return items[0].item || items[0].reference || "1 item";
  }
  const preview = items
    .slice(0, 2)
    .map((item) => item.item || item.reference)
    .filter(Boolean)
    .join(", ");
  if (items.length > 2) {
    return preview ? `${preview} (+${items.length - 2} more)` : `${items.length} items`;
  }
  return preview || `${items.length} items`;
}

export function formatPaymentPlanLabel(type: string | undefined): string {
  if (type === "UPFRONT_PAYMENT") return "Upfront";
  if (type === "INSTALLMENT") return "Installments";
  return type ?? "—";
}

export function getInvoiceHomeCurrency(invoice: iCustomerInvoice): string {
  return (
    invoice.pricingBreakdown?.homeCurrency ??
    invoice.pricingBreakdown?.sellingCurrency ??
    "GBP"
  );
}

export function getInvoiceTotalHomeCurrency(invoice: iCustomerInvoice): number {
  return Number(
    invoice.pricingBreakdown?.totalCostInHomeCurrency ??
      invoice.pricingBreakdown?.totalCost ??
      0
  );
}

export function getInvoiceBalanceFromTotalPaid(
  invoice: iCustomerInvoice
): number {
  const total = getInvoiceTotalAmount(invoice);
  const paid = invoice.totalPaid ?? 0;
  return Math.max(0, total - paid);
}

export function deriveInvoiceListStatus(
  invoice: iCustomerInvoice
): "OPEN" | "PAID" | "DRAFT" {
  const total = getInvoiceTotalAmount(invoice);
  if (total <= LIST_AMOUNT_TOLERANCE) return "DRAFT";
  const balance = getInvoiceBalanceFromTotalPaid(invoice);
  if (balance <= LIST_AMOUNT_TOLERANCE) return "PAID";
  return "OPEN";
}

export function getSupplierBillBalanceFromTotalPaid(
  bill: iSupplierBill
): number {
  const total = getSupplierBillTotalHomeCurrency(bill);
  const paid = bill.totalPaid ?? 0;
  return Math.max(0, total - paid);
}

export function deriveSupplierBillListStatus(
  bill: iSupplierBill
): SupplierBillPaymentStatus {
  if (bill.paymentStatus) {
    return bill.paymentStatus;
  }
  const total = getSupplierBillTotalHomeCurrency(bill);
  if (total <= LIST_AMOUNT_TOLERANCE) {
    return "NOT_PAID";
  }
  const paid = bill.totalPaid ?? 0;
  if (paid >= total - LIST_AMOUNT_TOLERANCE) {
    return "FULLY_PAID";
  }
  if (paid <= LIST_AMOUNT_TOLERANCE) {
    return "NOT_PAID";
  }
  return "PARTIALLY_PAID";
}

export function supplierBillStatusToBadgeVariant(
  status: SupplierBillPaymentStatus
): "not-paid" | "paid" | "partial" {
  if (status === "FULLY_PAID") return "paid";
  if (status === "PARTIALLY_PAID") return "partial";
  return "not-paid";
}

export function sumCustomerReceiptsInHomeCurrency(
  receipts: iCustomerPaymentReceipt[],
  options?: { refundsOnly?: boolean; paymentsOnly?: boolean }
): number {
  const useMagnitude = options?.refundsOnly === true || options?.paymentsOnly === true;
  return getNonVoidedReceipts(receipts)
    .filter((r) => {
      if (options?.refundsOnly) return r.isRefund === true;
      if (options?.paymentsOnly) return r.isRefund !== true;
      return true;
    })
    .reduce((sum, r) => {
      // Net holiday amount only — customer surcharges must not reduce invoice balance.
      const amount = getReceiptHolidayAmount(r);
      return sum + (useMagnitude ? Math.abs(amount) : amount);
    }, 0);
}

export function computeInvoiceRawBalance(
  invoice: iCustomerInvoice,
  receipts: iCustomerPaymentReceipt[]
): number {
  const total = getInvoiceTotalAmount(invoice);
  const paid = sumCustomerReceiptsInHomeCurrency(receipts, {
    paymentsOnly: true,
  });
  const refunded = sumCustomerReceiptsInHomeCurrency(receipts, {
    refundsOnly: true,
  });
  return total - paid + refunded;
}

export function computeInvoiceBalance(
  invoice: iCustomerInvoice,
  receipts: iCustomerPaymentReceipt[]
): number {
  return Math.max(0, computeInvoiceRawBalance(invoice, receipts));
}

export function computeInvoiceOverpaidAmount(
  invoice: iCustomerInvoice,
  receipts: iCustomerPaymentReceipt[]
): number {
  const rawBalance = computeInvoiceRawBalance(invoice, receipts);
  return rawBalance < -0.005 ? Math.abs(rawBalance) : 0;
}

export function deriveInvoiceStatus(
  invoice: iCustomerInvoice,
  receipts: iCustomerPaymentReceipt[]
): string {
  const total = getInvoiceTotalAmount(invoice);
  if (total <= 0) return "DRAFT";
  const rawBalance = computeInvoiceRawBalance(invoice, receipts);
  if (rawBalance < -0.005) return "OVERPAID";
  if (rawBalance <= 0.005) return "PAID";
  return "OPEN";
}

/** Bill total in home currency (source of truth: pricingBreakdown, not payment schedule). */
export function getSupplierBillTotalHomeCurrency(bill: iSupplierBill): number {
  return Number(
    bill.pricingBreakdown?.totalCostInHomeCurrency ??
      bill.pricingBreakdown?.totalCost ??
      0
  );
}

/** Bill total in supplier/original (selling) currency. */
export function getSupplierBillTotal(bill: iSupplierBill): number {
  return Number(
    bill.pricingBreakdown?.totalCost ??
      bill.pricingBreakdown?.totalCostInHomeCurrency ??
      0
  );
}

export function getSupplierBillCurrency(bill: iSupplierBill): string {
  return bill.pricingBreakdown?.sellingCurrency ?? "GBP";
}

export function getSupplierBillHomeCurrency(bill: iSupplierBill): string {
  return bill.pricingBreakdown?.homeCurrency ?? getSupplierBillCurrency(bill);
}

export function sumSupplierReceiptsInHomeCurrency(
  receipts: iSupplierBillPaymentReceipt[]
): number {
  return (receipts ?? [])
    .filter((r) => r.voided !== true)
    .reduce((sum, r) => sum + Number(r.amountInHomeCurrency ?? 0), 0);
}

const SUPPLIER_BILL_PAYMENT_STATUS_TOLERANCE = 0.005;

export function getSupplierBillTotalPaid(
  bill: iSupplierBill,
  receipts: iSupplierBillPaymentReceipt[]
): number {
  return bill.totalPaid != null
    ? Number(bill.totalPaid)
    : sumSupplierReceiptsInHomeCurrency(receipts);
}

export function computeSupplierBillBalance(
  bill: iSupplierBill,
  receipts: iSupplierBillPaymentReceipt[]
): number {
  const total = getSupplierBillTotalHomeCurrency(bill);
  const paid = getSupplierBillTotalPaid(bill, receipts);
  return Math.max(0, total - paid);
}

export function isSupplierBillOverpaid(
  bill: iSupplierBill,
  receipts: iSupplierBillPaymentReceipt[]
): boolean {
  const total = getSupplierBillTotalHomeCurrency(bill);
  const paid = getSupplierBillTotalPaid(bill, receipts);
  return (
    total > SUPPLIER_BILL_PAYMENT_STATUS_TOLERANCE &&
    paid > total + SUPPLIER_BILL_PAYMENT_STATUS_TOLERANCE
  );
}

export const SUPPLIER_BILL_PAYMENT_STATUS_LABELS: Record<
  SupplierBillPaymentStatus,
  string
> = {
  NOT_PAID: "Not paid",
  PARTIALLY_PAID: "Partially paid",
  FULLY_PAID: "Paid",
};

export function getSupplierBillPaymentStatus(
  bill: iSupplierBill,
  receipts: iSupplierBillPaymentReceipt[]
): SupplierBillPaymentStatus {
  if (bill.paymentStatus) {
    return bill.paymentStatus;
  }
  const total = getSupplierBillTotalHomeCurrency(bill);
  if (total <= SUPPLIER_BILL_PAYMENT_STATUS_TOLERANCE) {
    return "NOT_PAID";
  }
  const balance = computeSupplierBillBalance(bill, receipts);
  if (balance <= SUPPLIER_BILL_PAYMENT_STATUS_TOLERANCE) {
    return "FULLY_PAID";
  }
  const paid = getSupplierBillTotalPaid(bill, receipts);
  if (paid <= SUPPLIER_BILL_PAYMENT_STATUS_TOLERANCE) {
    return "NOT_PAID";
  }
  return "PARTIALLY_PAID";
}

export function formatSupplierBillPaymentStatusLabel(
  status: SupplierBillPaymentStatus
): string {
  return SUPPLIER_BILL_PAYMENT_STATUS_LABELS[status];
}

function paymentStatusToDisplayStatus(
  paymentStatus: SupplierBillPaymentStatus
): string {
  if (paymentStatus === "FULLY_PAID") return "PAID";
  return "OPEN";
}

export function deriveSupplierBillStatus(
  bill: iSupplierBill,
  receipts: iSupplierBillPaymentReceipt[]
): string {
  return paymentStatusToDisplayStatus(
    getSupplierBillPaymentStatus(bill, receipts)
  );
}
